import type { Component } from 'vue'
import {
  BanknotesIcon,
  CalendarDaysIcon,
  CubeIcon,
  EnvelopeIcon,
  FolderIcon,
  PhoneIcon,
  QuestionMarkCircleIcon,
  RocketLaunchIcon,
  UserGroupIcon,
} from '@heroicons/vue/24/outline'
import type { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import type { HubAccent } from '@/v2/data/sidebarMenu'

type PermissionStore = ReturnType<typeof useBoostCorePermissionStore>

/**
 * Raccourcis vers des outils **externes** (ODOO, Lucca, Microsoft 365…), affichés
 * en grille plate dans le panneau du header. Volontairement statique pour
 * l'instant (config en code, comme `sidebarMenu.ts`) : zéro fetch, conforme
 * RGESN, le gating par permissions suffit. Une admin DB pourra remplacer cette
 * source plus tard sans toucher le composant (il ne consomme que
 * `filterAppShortcuts`).
 *
 * Les URLs sont les vraies cibles de prod. Pour brancher le logo officiel d'un
 * outil (au lieu du Heroicon), cf `assets/app-logos/README.md`.
 */

export interface AppShortcut {
  /** Clé stable — sert d'id ARIA et de `key` de liste */
  id: string
  label: string
  /** Texte explicatif affiché en popover (survol/focus) — court, 1 phrase */
  description: string
  /**
   * Icône de tuile. Par défaut un Heroicon outline ; pour brancher le **logo
   * officiel** d'un outil, importer le SVG (`import OdooLogo from
   * '@/v2/assets/app-logos/odoo.svg'`) et l'affecter ici — cf README assets.
   */
  icon: Component
  /** Accent de marque de la tuile (jeton `HUB_ACCENTS`) — défaut : primary */
  accent?: HubAccent
  /** Lien externe (nouvel onglet) — exclusif avec `resolveHref` */
  href?: string
  /**
   * Lien externe dynamique résolu via le store (ex: URL selon la source de
   * l'utilisateur). Prioritaire sur `href` (cf `filterAppShortcuts`).
   */
  resolveHref?: (perms: PermissionStore) => string
  /** Visibilité selon permissions/flags (défaut : visible pour tous) */
  visible?: (perms: PermissionStore) => boolean
}

export const appShortcuts: AppShortcut[] = [
  {
    id: 'odoo',
    label: 'ODOO',
    description: 'ERP : devis, commandes, facturation et stock.',
    icon: CubeIcon,
    accent: 'deep',
    href: 'https://darbtech-pandora.odoo.com/',
  },
  {
    id: 'lucca',
    label: 'Lucca',
    description: 'RH : congés, notes de frais et entretiens.',
    icon: UserGroupIcon,
    accent: 'teal',
    href: 'https://pandora.ilucca.net/hello/',
  },
  {
    id: 'boostsales',
    label: 'BoostSales',
    description: 'Outil interne d’aide à la vente.',
    icon: RocketLaunchIcon,
    accent: 'coral',
    href: 'https://boost-sales.groupe-pandora.com/',
  },
  {
    id: 'pandora-lease',
    label: 'Pandora Lease',
    description: 'Espace partenaire — financement locatif.',
    icon: BanknotesIcon,
    accent: 'blue',
    href: 'https://espacepartenaire.pandora-lease.fr/',
  },
  {
    id: 'threecx',
    label: '3CX',
    description: 'Téléphonie d’entreprise (softphone et standard).',
    icon: PhoneIcon,
    accent: 'ardoise',
    href: 'https://pandora.my3cx.fr:5001/',
  },
  {
    id: 'outlook-mail',
    label: 'Outlook Mail',
    description: 'Messagerie Outlook (Microsoft 365).',
    icon: EnvelopeIcon,
    accent: 'blue',
    href: 'https://outlook.office.com/mail',
  },
  {
    id: 'outlook-calendar',
    label: 'Outlook Agenda',
    description: 'Agenda Outlook (Microsoft 365).',
    icon: CalendarDaysIcon,
    accent: 'teal',
    href: 'https://outlook.cloud.microsoft/calendar/',
  },
  {
    id: 'ariane',
    label: 'Ariane',
    description: 'Intranet SharePoint : documents et ressources.',
    icon: FolderIcon,
    accent: 'deep',
    href: 'https://groupepandora.sharepoint.com/sites/Ariane/',
  },
  {
    id: 'aide',
    label: 'Aide',
    description: 'Documentation et guides d’utilisation.',
    icon: QuestionMarkCircleIcon,
    accent: 'coral',
    href: 'https://doc.groupe-pandora.com/books/aide',
  },
]

/**
 * Filtre les raccourcis selon les permissions et résout les URLs dynamiques —
 * même logique que `filterSidebarMenu` (un raccourci sans droit disparaît).
 */
export function filterAppShortcuts(
  apps: AppShortcut[],
  perms: PermissionStore,
): AppShortcut[] {
  const result: AppShortcut[] = []
  for (const app of apps) {
    if (app.visible && !app.visible(perms)) continue
    result.push(app.resolveHref ? { ...app, href: app.resolveHref(perms) } : app)
  }
  return result
}
